import { useMemo } from "react";

/**
 * Plain monospace renderer for full config files (current / next nginx.conf).
 * Mirrors DiffView's chrome so the two views feel like siblings; the only
 * difference is no +/- tag column and no per-line tint.
 */
export function CodeView({
	code,
	emptyLabel = "(empty)",
}: {
	code: string;
	emptyLabel?: string;
}) {
	const lines = useMemo(() => {
		if (!code) return null;
		return code.split("\n").map((line, i) => ({
			key: `${i}:${line.slice(0, 32)}`,
			rest: line,
			lineNo: i + 1,
		}));
	}, [code]);

	return (
		<div className="overflow-hidden rounded-lg border border-zinc-800/90 ring-1 ring-zinc-950/80">
			<pre className="max-h-[min(58vh,38rem)] overflow-auto bg-zinc-950 p-4 font-mono text-[0.8125rem] leading-relaxed text-zinc-100 scrollbar-gutter-stable">
				{lines ? (
					lines.map(({ key, rest, lineNo }) => (
						<div
							key={key}
							className="flex min-w-0 gap-3 px-3 py-0.5 text-zinc-200 first:pt-1 last:pb-1"
						>
							<span className="w-10 shrink-0 select-none tabular-nums text-zinc-500">
								{String(lineNo).padStart(4, " ")}
							</span>
							<span className="min-w-0 flex-1 whitespace-pre-wrap wrap-break-word">
								{rest}
							</span>
						</div>
					))
				) : (
					<div className="px-3 py-2 text-zinc-500">{emptyLabel}</div>
				)}
			</pre>
		</div>
	);
}
